/**
 * Router Module
 *
 * Minimal client-side router using the History API for URL-based navigation.
 * Provides deep linking, browser history integration, and route matching.
 */

class Router {
    constructor() {
        this.routes = [];
        this.currentRoute = null;
        this.notFoundHandler = null;
        this.beforeEachGuards = [];

        // Bind methods to maintain context
        this.handlePopState = this.handlePopState.bind(this);
        this.handleLinkClick = this.handleLinkClick.bind(this);

        // Listen for browser back/forward
        window.addEventListener('popstate', this.handlePopState);

        // Listen for link clicks
        document.addEventListener('click', this.handleLinkClick);
    }

    /**
     * Register a route
     * @param {string} path - URL path pattern (e.g., '/learn/lessons/:lessonId')
     * @param {Object} config - Route configuration
     * @param {string} config.name - Route name for programmatic navigation
     * @param {string} config.pageId - Page ID to show
     * @param {Function} config.beforeEnter - Optional guard function
     * @param {Function} config.onEnter - Optional callback when route is entered
     * @param {Object} config.meta - Optional metadata (e.g., requiresAuth)
     */
    addRoute(path, config) {
        const pattern = this.pathToRegex(path);
        this.routes.push({
            path,
            pattern,
            params: this.extractParamNames(path),
            ...config
        });
    }

    /**
     * Convert path pattern to regex for matching
     * Supports :param syntax for dynamic segments
     */
    pathToRegex(path) {
        // Escape special regex characters except :param syntax
        const escaped = path
            .replace(/\//g, '\\/')
            .replace(/:\w+/g, '([^/]+)'); // :param becomes capturing group

        return new RegExp(`^${escaped}$`);
    }

    /**
     * Extract parameter names from path pattern
     */
    extractParamNames(path) {
        const matches = path.match(/:\w+/g);
        return matches ? matches.map(m => m.substring(1)) : [];
    }

    /**
     * Match URL path to a route
     * @param {string} path - URL path to match
     * @returns {Object|null} - Matched route with params or null
     */
    matchRoute(path) {
        for (const route of this.routes) {
            const match = path.match(route.pattern);

            if (match) {
                // Extract parameter values
                const params = {};
                route.params.forEach((paramName, index) => {
                    params[paramName] = match[index + 1];
                });

                return {
                    route,
                    params,
                    path
                };
            }
        }

        return null;
    }

    /**
     * Navigate to a URL path
     * @param {string} path - URL path to navigate to
     * @param {Object} options - Navigation options
     * @param {boolean} options.replace - Use replaceState instead of pushState
     * @param {Object} options.state - Additional state to store
     */
    async navigate(path, options = {}) {
        const { replace = false, state = {} } = options;

        // Match route
        const match = this.matchRoute(path);

        if (!match && this.notFoundHandler) {
            this.notFoundHandler(path);
            return;
        }

        if (!match) {
            console.warn(`No route matched for path: ${path}`);
            return;
        }

        // Run global guards
        for (const guard of this.beforeEachGuards) {
            const result = await guard(match, this.currentRoute);
            if (result === false) {
                // Guard blocked navigation
                return;
            }
            if (typeof result === 'string') {
                // Guard redirected
                this.navigate(result, options);
                return;
            }
        }

        // Run route-specific guard
        if (match.route.beforeEnter) {
            const result = await match.route.beforeEnter(match, this.currentRoute);
            if (result === false) {
                return;
            }
            if (typeof result === 'string') {
                this.navigate(result, options);
                return;
            }
        }

        // Update browser history
        const url = path;
        const historyState = { path, params: match.params, ...state };

        if (replace) {
            window.history.replaceState(historyState, '', url);
        } else {
            window.history.pushState(historyState, '', url);
        }

        // Update current route
        this.currentRoute = match;

        // Execute route callback
        if (match.route.onEnter) {
            await match.route.onEnter(match);
        }

        // Emit custom event for other components to listen
        window.dispatchEvent(new CustomEvent('route-changed', {
            detail: match
        }));
    }

    /**
     * Handle browser back/forward buttons
     */
    handlePopState(event) {
        const path = window.location.pathname;

        // Navigate without pushing to history (already changed by browser)
        const match = this.matchRoute(path);

        if (match) {
            this.currentRoute = match;

            if (match.route.onEnter) {
                match.route.onEnter(match);
            }

            window.dispatchEvent(new CustomEvent('route-changed', {
                detail: match
            }));
        } else if (this.notFoundHandler) {
            this.notFoundHandler(path);
        }
    }

    /**
     * Handle clicks on <a> tags with data-route attribute
     * Enables declarative routing without needing onclick handlers
     */
    handleLinkClick(event) {
        // Find closest anchor tag
        const link = event.target.closest('a[data-route]');

        if (link) {
            event.preventDefault();
            const path = link.getAttribute('href');
            this.navigate(path);
        }
    }

    /**
     * Register a global navigation guard
     * @param {Function} guard - Guard function (to, from) => boolean|string
     */
    beforeEach(guard) {
        this.beforeEachGuards.push(guard);
    }

    /**
     * Register a 404 handler
     * @param {Function} handler - Function to call when no route matches
     */
    onNotFound(handler) {
        this.notFoundHandler = handler;
    }

    /**
     * Get current route information
     */
    getCurrentRoute() {
        return this.currentRoute;
    }

    /**
     * Navigate to a named route
     * @param {string} name - Route name
     * @param {Object} params - Route parameters
     */
    navigateToName(name, params = {}) {
        const route = this.routes.find(r => r.name === name);

        if (!route) {
            console.warn(`No route found with name: ${name}`);
            return;
        }

        // Build path with parameters
        let path = route.path;
        for (const [key, value] of Object.entries(params)) {
            path = path.replace(`:${key}`, value);
        }

        this.navigate(path);
    }

    /**
     * Initialize router - call this after all routes are registered
     * Handles initial page load
     */
    async init() {
        // Handle current URL on page load
        const path = window.location.pathname;

        // Use replace for initial navigation to avoid duplicate history entry
        await this.navigate(path, { replace: true });
    }

    /**
     * Build URL with query parameters
     * @param {string} path - Base path
     * @param {Object} params - Query parameters
     */
    static buildUrl(path, params = {}) {
        const url = new URL(path, window.location.origin);

        for (const [key, value] of Object.entries(params)) {
            url.searchParams.set(key, value);
        }

        return url.pathname + url.search;
    }

    /**
     * Get query parameters from current URL
     */
    static getQueryParams() {
        const params = {};
        const searchParams = new URLSearchParams(window.location.search);

        for (const [key, value] of searchParams) {
            params[key] = value;
        }

        return params;
    }
}

// Export singleton instance
export const router = new Router();
